import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calendar, Download, Search, Filter, Loader2 } from "lucide-react";
import englishService from '../services/englishService';
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf"; // Import jsPDF

const ExportData = () => {
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTypes, setSelectedTypes] = useState([]);

  const recordTypes = [
    "Article",
    "Blog",
    "Event",
    "Interview",
    "News",
    "Project",
    "SCCNMessages",
    "Speech",
  ];

  useEffect(() => {
    const fetchAllRecords = async () => {
        setLoading(true);
        try {
            // Fetch data from all endpoints
            const [
                BlogManagement,
                ArticleManagement,
                NewsManagement,
                SccnMessageManagement,
                ProjectManagement,
                EventManagement,
                SpeechManagement,
            ] = await Promise.all([
                englishService.blogs.getAll(),
                englishService.articles.getAll(),
                englishService.news.getAll(),
                englishService.sccnMessages.getAll(),
                englishService.projects.getAll(),
                englishService.events.getAll(),
                englishService.speeches.getAll(),
            ]);

            // Combine all responses into a single array
            const allRecords = [
                (Array.isArray(BlogManagement.data) ? BlogManagement.data : []),
                (Array.isArray(ArticleManagement.data) ? ArticleManagement.data : []),
                (Array.isArray(NewsManagement.data) ? NewsManagement.data : []),
                (Array.isArray(SccnMessageManagement.data) ? SccnMessageManagement.data : []),
                (Array.isArray(ProjectManagement.data) ? ProjectManagement.data : []),
                (Array.isArray(EventManagement.data) ? EventManagement.data : []),
                (Array.isArray(SpeechManagement.data) ? SpeechManagement.data : []),
            ];

            // Set the combined records to state
            setRecords(allRecords);
            console.log("All Records:", allRecords);
            setFilteredRecords(allRecords);

            // Ensure records are displayed by default
            setError(null);
        } catch (error) {
            console.error("Error fetching records:", error);
            setError("Failed to load records");
        } finally {
            setLoading(false);
        }
    };

    fetchAllRecords();
}, []);
  

  const handleFilter = () => {
    console.log("Applying Filters...");
    console.log("Start Date:", startDate, "End Date:", endDate);
    console.log("Selected Types:", selectedTypes);
  
    const filtered = records.filter((record) => {
      if (!record.date) return false; // Ignore records with missing dates
  
      const recordDate = new Date(record.date);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;
  
      // Ensure date comparison is valid
      const dateMatch =
        (!start || (recordDate instanceof Date && recordDate >= start)) &&
        (!end || (recordDate instanceof Date && recordDate <= end));
  
      // Ensure type filtering is working
      const typeMatch =
        selectedTypes.length === 0 || selectedTypes.includes(record.type?.trim());
  
      // Ensure search is working correctly
      const searchMatch =
        !searchQuery ||
        record.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (record.content &&
          typeof record.content[0] === "string" &&
          record.content[0].toLowerCase().includes(searchQuery.toLowerCase()));
  
      return dateMatch && typeMatch && searchMatch;
    });
  
    console.log("Filtered Records:", filtered.length);
    setFilteredRecords(filtered);
  };
  

  const handleTypeToggle = (type) => {
    setSelectedTypes((prevTypes) =>
      prevTypes.includes(type)
        ? prevTypes.filter((t) => t !== type)
        : [...prevTypes, type]
    );
  };

  const downloadAsPdf = (record) => {
    const doc = new jsPDF();

    // Add title
    doc.setFontSize(18);
    doc.text(record.title, 10, 10);

    // Add type
    doc.setFontSize(14);
    doc.text(`Type: ${record.type}`, 10, 20);

    // Add date
    doc.text(`Date: ${new Date(record.date).toLocaleDateString()}`, 10, 30);

    // Add author
    doc.text(`Author: ${record.author || "N/A"}`, 10, 40);

    // Add content
    doc.setFontSize(12);
    const content = record.content?.[0] || "No content available";
    doc.text("Content:", 10, 50);
    doc.text(content, 10, 60, { maxWidth: 180 });

    // Save the PDF
    const filename = `${record.type}-${record.title.replace(/\s+/g, "_")}.pdf`;
    doc.save(filename);
  };

  const downloadSelectedAsZip = async (selectedRecords) => {
    if (selectedRecords.length === 0) {
      alert("Please select at least one record to download");
      return;
    }

    const zip = new JSZip();

    // Generate individual PDFs for each selected record
    for (const record of selectedRecords) {
      const doc = new jsPDF();

      // Add title
      doc.setFontSize(18);
      doc.text(record.title, 10, 10);

      // Add type
      doc.setFontSize(14);
      doc.text(`Type: ${record.type}`, 10, 20);

      // Add date
      doc.text(`Date: ${new Date(record.date).toLocaleDateString()}`, 10, 30);

      // Add author
      doc.text(`Author: ${record.author || "N/A"}`, 10, 40);

      // Add content
      doc.setFontSize(12);
      const content = record.content?.[0] || "No content available";
      doc.text("Content:", 10, 50);
      doc.text(content, 10, 60, { maxWidth: 180 });

      // Get PDF as blob
      const pdfBlob = doc.output("blob");
      const filename = `${record.type}-${record.title.replace(
        /\s+/g,
        "_"
      )}.pdf`;
      zip.file(filename, pdfBlob);
    }

    // Generate and download the zip file
    zip.generateAsync({ type: "blob" }).then((content) => {
      saveAs(content, "exported_records.zip");
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-sky-50 via-white to-blue-50">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center gap-4"
        >
          <Loader2 className="w-12 h-12 text-sky-500 animate-spin" />
          <p className="text-gray-600 animate-pulse">Loading records...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gradient-to-br from-sky-50 via-white to-blue-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-red-100 shadow-lg"
        >
          <p className="text-red-500 flex items-center gap-2">
            <span className="text-2xl">⚠️</span>
            {error}
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-blue-50 relative overflow-hidden">
      <div className="relative z-10">
        <div className="container mx-auto px-6 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="relative mb-8"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-sky-100/50 via-white/50 to-blue-100/50 rounded-3xl blur-2xl" />
            <div className="relative bg-white/30 backdrop-blur-md rounded-3xl p-8 border border-white/50 shadow-xl">
              <h1 className="text-4xl md:text-5xl text-center font-bold bg-gradient-to-r from-sky-600 to-blue-600 bg-clip-text text-transparent mb-6">
                Export Data Management
              </h1>
              <p className="text-lg text-center text-gray-600 max-w-3xl mx-auto">
                View, filter, and export records with ease. Select the records
                you need and download them in your preferred format.
              </p>
            </div>
          </motion.div>

          {/* Filters Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 mb-8 shadow-lg"
          >
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">
              Filters
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              {/* Date Range Filter */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Start Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="pl-10 w-full rounded-lg border border-gray-300 p-2 focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition-all"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  End Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="pl-10 w-full rounded-lg border border-gray-300 p-2 focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition-all"
                  />
                </div>
              </div>

              {/* Search Filter */}
              <div className="space-y-2 col-span-1 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  Search
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by title or content..."
                    className="pl-10 w-full rounded-lg border border-gray-300 p-2 focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Type Checkboxes */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content Types
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {recordTypes.map((type) => (
                  <label key={type} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedTypes.includes(type)}
                      onChange={() => handleTypeToggle(type)}
                      className="rounded text-sky-500 focus:ring-sky-500 transition-all"
                    />
                    <span className="text-sm text-gray-700">{type}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Apply Filter Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleFilter}
              className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-2 rounded-lg shadow flex items-center justify-center transition-all duration-300"
            >
              <Filter className="mr-2 h-4 w-4" />
              Apply Filters
            </motion.button>
          </motion.div>

          {/* Results Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-lg"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold text-gray-800">
                Results ({filteredRecords.length})
              </h2>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() =>
                  downloadSelectedAsZip(
                    filteredRecords.filter((r) => r.selected)
                  )
                }
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg shadow flex items-center transition-all duration-300"
              >
                <Download className="mr-2 h-4 w-4" />
                Download Selected
              </motion.button>
            </div>

            {filteredRecords.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  No records found. Try adjusting your filters.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {filteredRecords.map((record, index) => (
                  <motion.div
                    key={record._id || index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-all duration-300 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
                  >
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2">
                        <div className="flex items-center gap-4 mb-2 sm:mb-0">
                          <input
                            type="checkbox"
                            checked={record.selected || false}
                            onChange={() => {
                              const updatedRecords = [...filteredRecords];
                              const recordIndex = updatedRecords.findIndex(
                                (r) => r._id === record._id
                              );
                              updatedRecords[recordIndex] = {
                                ...updatedRecords[recordIndex],
                                selected: !updatedRecords[recordIndex].selected,
                              };
                              setFilteredRecords(updatedRecords);
                            }}
                            className="rounded text-sky-500 focus:ring-sky-500 transition-all h-4 w-4"
                          />
                          <span className="px-2 py-1 text-xs font-medium bg-sky-100 text-sky-800 rounded-full">
                            {record.type}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <Calendar className="mr-1 h-3 w-3" />
                          {new Date(record.date).toLocaleDateString()}
                        </div>
                      </div>
                      <h3 className="text-lg font-medium text-gray-800 mb-1">
                        {record.title}
                      </h3>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {record.content?.[0] || "No content available"}
                      </p>
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => downloadAsPdf(record)}
                      className="bg-sky-500 hover:bg-sky-600 text-white px-3 py-1.5 rounded-lg flex items-center shadow-sm transition-all duration-300 self-end sm:self-auto"
                    >
                      <Download className="mr-1 h-4 w-4" />
                      Download
                    </motion.button>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ExportData;
